import React, { useMemo, useState } from 'react'
import { Drawer, Radio, Row, Space, Typography } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import {
	Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis
} from 'recharts'
import moment from 'moment'
import { ITelephoneSurveyTableItem, TELEPHONE_SURVEY_CALL_STATUS, TELEPHONE_SURVEY_SATISFACTION } from '../../../reducers/telephoneSurveys/actions'

interface Props {
	open: boolean
	onClose: () => void
	data: ITelephoneSurveyTableItem[]
}

type GroupMode = 'month' | 'week'

interface ChartEntry {
	period: string
	sortKey: number
	SATISFIED: number
	UNSATISFIED: number
	NO_ANSWER: number
	CALL_LATER: number
	details: Record<string, string[]>
}

const COLORS = {
	SATISFIED: '#15803d',
	UNSATISFIED: '#dc2626',
	NO_ANSWER: '#c2410c',
	CALL_LATER: '#1d4ed8'
}

const LABELS: Record<string, string> = {
	SATISFIED: 'Spokojný',
	UNSATISFIED: 'Nespokojný',
	NO_ANSWER: 'Nezodvihol',
	CALL_LATER: 'Zavolať neskôr'
}

/** Returns ISO week number and ISO week-year without relying on any dayjs plugin. */
const getISOWeekYear = (date: Date): { week: number; year: number } => {
	const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
	const dayNum = d.getUTCDay() || 7 // Mon=1 … Sun=7
	d.setUTCDate(d.getUTCDate() + 4 - dayNum) // shift to nearest Thursday
	const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
	const diffDays = (d.getTime() - yearStart.getTime()) / 86400000
	const week = Math.ceil((diffDays + 1) / 7)
	return { week, year: d.getUTCFullYear() }
}

const getSurveyKey = (survey: ITelephoneSurveyTableItem): string => {
	if (survey.callStatus === TELEPHONE_SURVEY_CALL_STATUS.NO_ANSWER) return 'NO_ANSWER'
	if (survey.callStatus === TELEPHONE_SURVEY_CALL_STATUS.CALL_LATER) return 'CALL_LATER'
	if (survey.satisfaction === TELEPHONE_SURVEY_SATISFACTION.SATISFIED) return 'SATISFIED'
	if (survey.satisfaction === TELEPHONE_SURVEY_SATISFACTION.UNSATISFIED) return 'UNSATISFIED'
	return 'NO_ANSWER'
}

const TelephoneSurveyChartDrawer: React.FC<Props> = ({ open, onClose, data }) => {
	const [groupMode, setGroupMode] = useState<GroupMode>('month')
	const [activeKey, setActiveKey] = useState<string | null>(null)

	const chartData = useMemo<ChartEntry[]>(() => {
		const map = new Map<string, ChartEntry>()

		data.forEach((survey) => {
			if (!survey.calledAt) return
			const m = moment(survey.calledAt, 'DD.MM.YYYY HH:mm')
			if (!m.isValid()) return

			const date = m.toDate()
			let period: string
			let sortKey: number

			if (groupMode === 'month') {
				// MM/YYYY — safe format, no plugin needed
				period = m.format('MM/YYYY')
				sortKey = new Date(date.getFullYear(), date.getMonth(), 1).getTime()
			} else {
				const { week, year } = getISOWeekYear(date)
				period = `T${week} ${year}`
				// Sort key = Monday of that ISO week
				const weekOffset = (week - 1) * 7
				const monday = new Date(Date.UTC(year, 0, 1 + weekOffset))
				const dow = monday.getUTCDay() || 7
				monday.setUTCDate(monday.getUTCDate() + 1 - dow)
				sortKey = monday.getTime()
			}

			if (!map.has(period)) {
				map.set(period, {
					period,
					sortKey,
					SATISFIED: 0,
					UNSATISFIED: 0,
					NO_ANSWER: 0,
					CALL_LATER: 0,
					details: { SATISFIED: [], UNSATISFIED: [], NO_ANSWER: [], CALL_LATER: [] }
				})
			}

			const entry = map.get(period)!
			const key = getSurveyKey(survey)
			entry[key as keyof Pick<ChartEntry, 'SATISFIED' | 'UNSATISFIED' | 'NO_ANSWER' | 'CALL_LATER'>] += 1
			const address = survey.jobAddresses || survey.organizationName || '-'
			entry.details[key].push(address)
		})

		return Array.from(map.values()).sort((a, b) => a.sortKey - b.sortKey)
	}, [data, groupMode])

	const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
		if (!active || !payload?.length || !activeKey) return null
		const entry = payload.find((p) => p.dataKey === activeKey)
		if (!entry) return null

		const details: string[] = (payload[0]?.payload?.details?.[activeKey] || [])

		return (
			<div style={{
				background: '#fff',
				border: '1px solid #e5e7eb',
				borderRadius: 8,
				padding: '10px 14px',
				boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
				maxWidth: 280
			}}>
				<div style={{ fontWeight: 700, marginBottom: 6, color: '#374151' }}>{label}</div>
				<div style={{ color: entry.color as string, fontWeight: 600, marginBottom: 4 }}>
					{LABELS[activeKey]}: {entry.value}
				</div>
				{details.length > 0 && (
					<ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#4b5563' }}>
						{details.slice(0, 8).map((d, i) => <li key={i}>{d}</li>)}
						{details.length > 8 && <li style={{ color: '#9ca3af' }}>+{details.length - 8} ďalších</li>}
					</ul>
				)}
			</div>
		)
	}

	return (
		<Drawer
			title={
				<Row justify="space-between" align="middle">
					<Space>
						<CloseOutlined style={{ color: '#aaa' }} onClick={onClose} />
						<Typography.Text strong>Graf prieskumov</Typography.Text>
					</Space>
					<Radio.Group
						value={groupMode}
						onChange={(e) => setGroupMode(e.target.value)}
						size="small"
					>
						<Radio.Button value="month">Mesiace</Radio.Button>
						<Radio.Button value="week">Týždne</Radio.Button>
					</Radio.Group>
				</Row>
			}
			width={820}
			visible={open}
			onClose={onClose}
			closeIcon={null}
		>
			{chartData.length === 0 ? (
				<div style={{ textAlign: 'center', color: '#9ca3af', padding: 48 }}>
					Žiadne dáta pre aktuálny filter
				</div>
			) : (
				<ResponsiveContainer width="100%" height={420}>
					<BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 50 }}>
						<CartesianGrid strokeDasharray="4 4" />
						<XAxis dataKey="period" tick={{ fontSize: 12 }} />
						<YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
						<Tooltip content={<CustomTooltip />} />
						<Legend verticalAlign="bottom" height={36} wrapperStyle={{ paddingTop: 16 }} />
						{(['SATISFIED', 'UNSATISFIED', 'NO_ANSWER', 'CALL_LATER'] as const).map((key) => (
							<Bar
								key={key}
								name={LABELS[key]}
								dataKey={key}
								stackId="a"
								fill={COLORS[key]}
								onMouseOver={() => setActiveKey(key)}
								onMouseOut={() => setActiveKey(null)}
							/>
						))}
					</BarChart>
				</ResponsiveContainer>
			)}
		</Drawer>
	)
}

export default TelephoneSurveyChartDrawer
